import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import ImageUpload from '@/components/ImageUpload';
import ImageCropper from '@/components/ImageCropper';
import Modal from '@/components/Modal';
import { storeTempImage, clearTempImage, isTempImageUrl, getTempImage } from '@/components/common/TempImageStorage';
import { getSafeImageUrl } from '@/utils/image-utils';

interface OptionImageUploadProps {
  currentImageUrl?: string | null;
  onImageUpdated: (newImageUrl: string) => void;
  optionId: string;
}

export default function OptionImageUpload({ 
  currentImageUrl, 
  onImageUpdated,
  optionId
}: OptionImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Update preview when currentImageUrl changes
  useEffect(() => {
    if (currentImageUrl !== previewUrl) {
      setPreviewUrl(currentImageUrl || null);
    }
  }, [currentImageUrl, previewUrl]);

  const handleImageSelect = async (file: File) => {
    setSelectedFile(file);
    setTempImageUrl(URL.createObjectURL(file));
    setShowCropper(true);
    return null; // Return null as we're not uploading yet
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      setIsUploading(true);
      setShowCropper(false);
      
      // Store the cropped image in memory and get a temporary URL
      const tempId = `option-${optionId}`;
      const tempUrl = storeTempImage(tempId, croppedBlob);
      
      // Set the preview to show the cropped image
      setPreviewUrl(tempUrl);
      
      // Pass the temporary URL to the parent component
      onImageUpdated(tempUrl);
      
      toast.success('Image cropped successfully');
    } catch (error) {
      console.error(`Error processing image for option ${optionId}:`, error);
      toast.error('Failed to process image');
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      if (tempImageUrl) {
        URL.revokeObjectURL(tempImageUrl);
        setTempImageUrl(null);
      }
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedFile(null);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
    }
  };

  const handleRemoveImage = async () => {
    try {
      setIsUploading(true);
      
      // If it's a temporary URL, clear it from memory
      if (previewUrl && isTempImageUrl(previewUrl)) {
        clearTempImage(`option-${optionId}`);
      }
      
      // Update UI
      setPreviewUrl(null);
      onImageUpdated('');
      toast.success('Image removed');
    } catch (error) {
      console.error(`Error removing image for option ${optionId}:`, error);
      toast.error('Failed to remove image');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <ImageUpload
        onImageUpload={handleImageSelect}
        onImageRemove={handleRemoveImage}
        currentImageUrl={previewUrl}
        className="h-40"
        maxSizeMB={1.5}
        shape="square"
        isUploading={isUploading}
      />
      
      <Modal
        isOpen={showCropper}
        onClose={handleCropCancel}
        title="Crop Image"
      >
        {tempImageUrl && (
          <ImageCropper
            imageUrl={tempImageUrl}
            aspect={16/9}
            onCropComplete={handleCropComplete}
            onCancel={handleCropCancel}
          />
        )}
      </Modal>
    </>
  );
}